import { Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, computed, effect, inject } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import {
  CmBaseCompoundFilterIntl,
  CmObjectFieldList,
  CmObjectFieldListData,
  CmObjectFieldListIntl,
  CmObjectListData,
  CmPaginateTableIntl,
  CmUiCompoundFilterIntl,
  CmUiTableIntl,
  TopBarIntl,
} from '@cm/ui-modules';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subscription, of, map, switchMap, tap } from 'rxjs';
import { LANG } from './core/injectors';
import { GetCustomFieldDefinitionResponseGQL, GetFieldsGQL } from './shared/graphql';
import { ObjectName } from '@workflow/models/view-model';
import { GetFieldsService } from './shared/services/get-fields.service';
import { ObjectListDataService } from './shared/services/object-list-data.service';
import { FrontendTranslateService } from './shared/services/translation.service';
import { InactivityMonitorService } from './shared/services/inactivitymonitor.service';
import { BackendStatusService } from './shared/services/backend-status.service';
import { RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AsyncTaskProgressComponent } from './shared/components/async-task-progress/async-task-progress.component';
import { BackendErrorComponent } from './shared/components/backend-error/backend-error.component';
import { SystemUpdatesPanelComponent } from './shared/components/system-updates/system-updates-panel/system-updates-panel.component';
import { AccessService } from '@cm/ui-modules';
import { BatchJobService } from './modules/batch-jobs/batch-job-service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    AsyncTaskProgressComponent,
    BackendErrorComponent,
    SystemUpdatesPanelComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private inactivityMonitor = inject(InactivityMonitorService);
  private backendStatusService = inject(BackendStatusService);
  private accessService = inject(AccessService);

  translateReady$: Observable<boolean>;
  backendAvailable$: Observable<boolean>;
  // Follows the session live (root AccessService: set by the login, the cookie bootstrap and the
  // refresh; cleared on logout) — not a one-off read at startup, which missed a login made without
  // a reload. Never read a TokenService injected here: a module provider in the import graph once
  // gave this component its own empty copy.
  hasValidSession = computed(() => !!this.accessService.user().active);
  private currentLocale: string;
  private readonly batchJobService = inject(BatchJobService);
  private batchJobStreamSub: Subscription | null = null;

  constructor() {
    // Open the central batch-job SSE stream as soon as a session exists, so all exports are tracked
    // even before the user visits /batch-job; close it when the session ends.
    effect(() => {
      if (this.hasValidSession() && !this.batchJobStreamSub) {
        this.batchJobStreamSub = this.batchJobService.stream$.subscribe({ error: () => {} });
      } else if (!this.hasValidSession() && this.batchJobStreamSub) {
        this.batchJobStreamSub.unsubscribe();
        this.batchJobStreamSub = null;
      }
    });
    const elementRef = inject(ElementRef);
    const translate = inject(FrontendTranslateService);
    const cmObjectListData = inject(CmObjectListData);
    const cmObjectFieldList = inject(CmObjectFieldList);
    const objectListDataService = inject(ObjectListDataService);
    const getFieldsService = inject(GetFieldsService);
    const cookieService = inject(CookieService);
    const cmUiCompoundFilterIntl = inject(CmUiCompoundFilterIntl);
    const cmUiTableIntl = inject(CmUiTableIntl);
    const panelTopBarIntl = inject(TopBarIntl);
    const paginatorIntl = inject(MatPaginatorIntl);
    const filterIntl = inject(CmBaseCompoundFilterIntl);
    const paginationTableIntl = inject(CmPaginateTableIntl);
    const objectFieldIntl = inject(CmObjectFieldListIntl);
    const cmObjectFieldListData = inject(CmObjectFieldListData);
    const getCustomFieldDefinitionResponseGQL = inject(GetCustomFieldDefinitionResponseGQL);
    const getFieldsGQL = inject(GetFieldsGQL);
    let lang = inject(LANG);

    const appRootElement = elementRef.nativeElement as HTMLElement;
    this.currentLocale = cookieService.get('locale') || 'en';
    lang = this.currentLocale;

    // Monitor backend availability
    this.backendAvailable$ = this.backendStatusService.backendAvailable$;
    translate.onLangChange.subscribe(() => {
      translateFilter(filterIntl, translate);
      translateObjectField(objectFieldIntl, translate);
      translatePaginationTable(paginationTableIntl, translate);
      translatePaginator(paginatorIntl, translate);
      translatePanelTopBar(panelTopBarIntl, translate);
      translateUiTable(cmUiTableIntl, translate);
      translateFilters(cmUiCompoundFilterIntl, translate);
    });

    this.translateReady$ = of(null).pipe(
      tap(() => translate.setDefaultLang('en')),
      switchMap(() => translate.use(lang)),
      tap(() => {
        translateFilter(filterIntl, translate);
        translateObjectField(objectFieldIntl, translate);
        translatePaginationTable(paginationTableIntl, translate);
        translatePaginator(paginatorIntl, translate);
        translatePanelTopBar(panelTopBarIntl, translate);
        translateUiTable(cmUiTableIntl, translate);
        translateFilters(cmUiCompoundFilterIntl, translate);
      }),
      map(() => true),
    );

    cmObjectListData.getObjectList = (objectName, searchValue, selectedIds) =>
      objectListDataService.getObjectList(objectName as ObjectName, searchValue, selectedIds);

    cmObjectFieldList.getObjectFields = (objectName) =>
      getFieldsService.getFieldsToObjectFieldOption(objectName as ObjectName);

    cmObjectFieldListData.getObjectCustomFields = (objectName) =>
      getCustomFieldDefinitionResponseGQL
        .fetch({ variables: { objectName }, fetchPolicy: 'no-cache', errorPolicy: 'all' })
        .pipe(map((response) => response.data));

    cmObjectFieldListData.getObjectDefaultFields = (objectName) =>
      getFieldsGQL
        .fetch({ variables: { objectName }, fetchPolicy: 'no-cache', errorPolicy: 'all' })
        .pipe(map((response) => response.data));
  }

  ngOnInit() {
    this.inactivityMonitor.startMonitoring();
  }

  ngOnDestroy() {
    this.inactivityMonitor.stopMonitoring();
    this.batchJobStreamSub?.unsubscribe();
  }
}

//
function translateFilter(intl: CmBaseCompoundFilterIntl, service: FrontendTranslateService): void {
  intl.lessThan = service.instant('ADMIN.operator.lessThan');
  intl.greaterThan = service.instant('ADMIN.operator.greaterThan');
  intl.lessThanOrEqualTo = service.instant('ADMIN.operator.lessThanOrEqualTo');
  intl.greaterThanOrEqualsTo = service.instant('ADMIN.operator.greaterThanOrEqualsTo');
  intl.equals = service.instant('ADMIN.operator.equals');
  intl.notEquals = service.instant('ADMIN.operator.notEquals');
  intl.startsWith = service.instant('ADMIN.operator.startsWith');
  intl.endWith = service.instant('ADMIN.operator.endWith');
  intl.contains = service.instant('ADMIN.operator.contains');
  intl.exists = service.instant('ADMIN.operator.exists');
  intl.notStartsWith = service.instant('ADMIN.operator.notStartsWith');
  intl.notEndsWith = service.instant('ADMIN.operator.notEndsWith');
  intl.notContains = service.instant('ADMIN.operator.notContains');
  intl.notExists = service.instant('ADMIN.operator.notExists');
  intl.and = service.instant('ADMIN.common.filter.and');
  intl.or = service.instant('ADMIN.common.filter.or');
  intl.value = service.instant('ADMIN.common.filter.value');
  intl.operator = service.instant('ADMIN.common.filter.operator');
  intl.changes.next();
}

function translateObjectField(intl: CmObjectFieldListIntl, service: FrontendTranslateService) {
  intl.objectName = service.instant('ADMIN.common.filter.objectName');
  intl.objectField = service.instant('ADMIN.common.filter.objectField');
  intl.filter = service.instant('ADMIN.common.filter.filter');
  intl.reset = service.instant('ADMIN.common.filter.reset');
  intl.defineFilter = service.instant('ADMIN.common.filter.defineFilter');
  intl.value = service.instant('ADMIN.common.filter.value');
  intl.yes = service.instant('ADMIN.common.filter.yes');
  intl.no = service.instant('ADMIN.common.filter.no');
  intl.getObjectName = (objectName) => service.instant(`API.${objectName}Label`);
  intl.getPropertyName = (objectName, propertyName) =>
    service.instant(`API.${objectName}.${propertyName}`);
  intl.getPropertyValue = (objectName, propertyName, value) =>
    service.instant(`API.${objectName}.${propertyName}.${value}`);
  intl.changes.next();
}

function translatePaginationTable(intl: CmPaginateTableIntl, service: FrontendTranslateService) {
  intl.noData = service.instant('ADMIN.common.table.noData');
  intl.filter = service.instant('ADMIN.common.table.filter');
  intl.columns = service.instant('ADMIN.common.table.columns');
  intl.actions = service.instant('ADMIN.common.table.actions');
  intl.search = service.instant('ADMIN.common.table.search');
  intl.changes.next();
}

function translatePaginator(intl: MatPaginatorIntl, service: FrontendTranslateService): void {
  intl.itemsPerPageLabel = service.instant('paginator.itemsPerPageLabel');
  intl.nextPageLabel = service.instant('paginator.nextPageLabel');
  intl.previousPageLabel = service.instant('paginator.previousPageLabel');
  intl.firstPageLabel = service.instant('paginator.firstPageLabel');
  intl.lastPageLabel = service.instant('paginator.lastPageLabel');
  intl.changes.next();
}

function translatePanelTopBar(intl: TopBarIntl, service: FrontendTranslateService): void {
  intl.accountSettings = service.instant('ADMIN.accountSettings');
  intl.logout = service.instant('ADMIN.logout');
  intl.presentationsManager = service.instant('ADMIN.presentationsManager');
  intl.highpManager = service.instant('ADMIN.highpManager');
  intl.datusoManager = service.instant('ADMIN.datusoManager');
  intl.challengeManager = service.instant('ADMIN.challengeManager');
  intl.courseManager = service.instant('ADMIN.courseManager');
  intl.contactManager = service.instant('ADMIN.contactManager');
  intl.automator = service.instant('ADMIN.automator');
  intl.sfdc = service.instant('ADMIN.sfdc');
  intl.administrationManager = service.instant('ADMIN.administrationManager');
  intl.presentations = service.instant('ADMIN.presentations');
  intl.meetings = service.instant('ADMIN.meetings');
  intl.presentationMedia = service.instant('ADMIN.presentationMedia');
  intl.highpCampaigns = service.instant('ADMIN.highpCampaigns');
  intl.highpMedia = service.instant('ADMIN.highpMedia');
  intl.datusoCampaigns = service.instant('ADMIN.datusoCampaigns');
  intl.datusoForms = service.instant('ADMIN.datusoForms');
  intl.courses = service.instant('ADMIN.courses');
  intl.courseLibraries = service.instant('ADMIN.courseLibraries');
  intl.courseCategories = service.instant('ADMIN.courseCategories');
  intl.leads = service.instant('ADMIN.leads');
  intl.contacts = service.instant('ADMIN.contacts');
  intl.workplaces = service.instant('ADMIN.workplaces');
  intl.groups = service.instant('ADMIN.groups');
  intl.specializations = service.instant('ADMIN.specializations');
  intl.customFields = service.instant('ADMIN.customFields');
  intl.teamMembers = service.instant('ADMIN.teamMembers');
  intl.batchJobs = service.instant('ADMIN.batchJobs');
  intl.batchJobsContactManager = service.instant('ADMIN.batchJobsContactManager');
  intl.applicationVersions = service.instant('ADMIN.applicationVersions');
  intl.billing = service.instant('ADMIN.billing');
}
function translateUiTable(intl: CmUiTableIntl, translate: FrontendTranslateService) {
  intl.filterInUse = translate.instant('uiTable.filterInUse');
  intl.search = translate.instant('uiTable.search');
  intl.sort = translate.instant('uiTable.sort');
  intl.more = translate.instant('uiTable.more');
  intl.columns = translate.instant('uiTable.columns');
  intl.basicColumns = translate.instant('uiTable.basicColumns');
  intl.customColumns = translate.instant('uiTable.customColumns');
  intl.basicMobileColumns = translate.instant('uiTable.basicMobileColumns');
  intl.customMobileColumns = translate.instant('uiTable.customMobileColumns');
  intl.apply = translate.instant('uiTable.apply');
}

function translateFilters(
  cmUiCompoundFilterIntl: CmUiCompoundFilterIntl,
  translateService: FrontendTranslateService,
): void {
  cmUiCompoundFilterIntl.addFilter = translateService.instant('ADMIN.common.filter.defineFilter');
  cmUiCompoundFilterIntl.filter = translateService.instant('ADMIN.common.filter.filter');
  cmUiCompoundFilterIntl.title = translateService.instant('ADMIN.common.filter.title');
  cmUiCompoundFilterIntl.reset = translateService.instant('ADMIN.common.filter.reset');
  cmUiCompoundFilterIntl.object = translateService.instant('ADMIN.common.filter.objectName');
  cmUiCompoundFilterIntl.field = translateService.instant('ADMIN.common.filter.objectField');
  cmUiCompoundFilterIntl.value = translateService.instant('ADMIN.common.filter.value');
  cmUiCompoundFilterIntl.operator = translateService.instant('ADMIN.common.filter.operator');
  cmUiCompoundFilterIntl.predefinedFilters = translateService.instant(
    'ADMIN.common.filter.predefinedFilters',
  );
  cmUiCompoundFilterIntl.contains = translateService.instant('ADMIN.operator.contains');
  cmUiCompoundFilterIntl.lessThan = translateService.instant('ADMIN.operator.lessThan');
  cmUiCompoundFilterIntl.greaterThan = translateService.instant('ADMIN.operator.greaterThan');
  cmUiCompoundFilterIntl.lessThanOrEqualTo = translateService.instant(
    'ADMIN.operator.lessThanOrEqualTo',
  );
  cmUiCompoundFilterIntl.equals = translateService.instant('ADMIN.operator.equals');
  cmUiCompoundFilterIntl.notEquals = translateService.instant('ADMIN.operator.notEquals');
  cmUiCompoundFilterIntl.startsWith = translateService.instant('ADMIN.operator.startsWith');
  cmUiCompoundFilterIntl.endWith = translateService.instant('ADMIN.operator.endWith');
  cmUiCompoundFilterIntl.exists = translateService.instant('ADMIN.operator.exists');
  cmUiCompoundFilterIntl.notStartsWith = translateService.instant('ADMIN.operator.notStartsWith');
  cmUiCompoundFilterIntl.notEndsWith = translateService.instant('ADMIN.operator.notEndsWith');
  cmUiCompoundFilterIntl.notContains = translateService.instant('ADMIN.operator.notContains');
  cmUiCompoundFilterIntl.notExists = translateService.instant('ADMIN.operator.notExists');
  cmUiCompoundFilterIntl.greaterThanOrEqualsTo = translateService.instant(
    'ADMIN.operator.greaterThanOrEqualsTo',
  );
  cmUiCompoundFilterIntl.isNull = translateService.instant('ADMIN.operator.isNull');
  cmUiCompoundFilterIntl.isNotNull = translateService.instant('ADMIN.operator.isNotNull');
}
